import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CTABanner from '../components/CTABanner';
import { Shield, Users, MapPin, Award, Target, Heart } from 'lucide-react';

const values = [
  { icon: Shield, title: 'Safety First', desc: 'Every worker is properly trained and WCB-compliant before placement. We prioritize workplace safety above all else.' },
  { icon: Users, title: 'People-Centred', desc: 'We treat every worker and employer as a partner, not a number. Long-term relationships are our foundation.' },
  { icon: Target, title: 'Reliability', desc: 'When we say workers will be there, they will be there. Our reputation is built on consistent, dependable service.' },
  { icon: Award, title: 'Quality', desc: 'Every candidate undergoes thorough screening and vetting. We send qualified, prepared workers to every assignment.' },
  { icon: MapPin, title: 'Locally Rooted', desc: 'Based in Vancouver, we understand BC\u2019s unique labour market, regional demands, and industry dynamics.' },
  { icon: Heart, title: 'Integrity', desc: 'Transparent pricing, honest communication, and fair treatment for every worker and every employer.' },
];

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>About WorkNation Labour Ltd | Vancouver Staffing Agency</title>
        <meta name="description" content="Learn about WorkNation Labour Ltd, a Vancouver-based staffing agency with 200+ workers placed across BC. We specialize in construction, warehouse, agriculture, transportation, and cold storage staffing." />
        <link rel="canonical" href="https://worknation.ca/about" />
      </Helmet>
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-16 px-6 bg-gradient-to-br from-white via-gray-50 to-blue-50">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#003366] mb-6">About WorkNation Labour Ltd</h1>
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-3xl">
              WorkNation Labour Ltd is a staffing agency headquartered in Vancouver, British Columbia. We connect qualified workers with employers who need reliable, skilled staff across the province. With <strong>200+ workers placed</strong> in construction, warehouse, agriculture, transportation, and cold storage, we are a trusted workforce partner for businesses of all sizes.
            </p>
          </div>
        </section>

        {/* Our Story */}
        <section className="py-16 px-6 bg-white">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-[#003366] mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  WorkNation Labour Ltd was born from a simple observation: businesses across British Columbia struggle to find reliable, qualified workers when they need them most. At the same time, hardworking people are looking for steady, well-paying jobs that fit their skills and schedules.
                </p>
                <p>
                  We set out to bridge that gap. Based in Vancouver, WorkNation has grown into a trusted staffing partner serving employers and workers across the entire province. From construction sites in Metro Vancouver to warehouses in Surrey, agricultural operations in the Fraser Valley, and cold storage facilities throughout BC, our team matches the right workers with the right roles every day.
                </p>
                <p>
                  What sets us apart is our commitment to people. We don&apos;t just fill positions\u2014we build relationships. We take the time to understand each employer&apos;s unique needs and each worker&apos;s skills, preferences, and career goals. The result is better placements, lower turnover, and stronger outcomes for everyone.
                </p>
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#003366] mb-6">Our Mission</h2>
              <div className="bg-[#003366] text-white p-8 rounded-2xl shadow-lg">
                <p className="text-lg leading-relaxed mb-4">
                  To be British Columbia&apos;s most trusted staffing partner by connecting qualified workers with employers through reliable, transparent, and people-first workforce solutions.
                </p>
                <p className="text-blue-200 text-base">
                  We believe every worker deserves fair pay and a safe workplace, and every employer deserves dependable staff who show up ready to work.
                </p>
              </div>
              <div className="mt-8 bg-gray-50 p-6 rounded-xl border-l-4 border-[#003366]">
                <h3 className="text-xl font-bold text-[#003366] mb-3">By the Numbers</h3>
                <ul className="space-y-2 text-gray-700">
                  <li><strong>200+</strong> workers placed across BC</li>
                  <li><strong>5</strong> specialized industries served</li>
                  <li><strong>10+</strong> cities and regions covered</li>
                  <li><strong>100%</strong> WCB and WorkSafe BC compliant</li>
                  <li><strong>24-48 hour</strong> average worker placement time</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] text-center mb-12">Our Values</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {values.map((v, i) => (
                <div key={i} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
                  <v.icon className="text-[#003366] mb-3" size={32} aria-hidden="true" />
                  <h3 className="text-xl font-bold text-[#003366] mb-2">{v.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 px-6 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] text-center mb-12">How WorkNation Works</h2>
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h3 className="text-2xl font-bold text-[#003366] mb-6">For Employers</h3>
                <ol className="space-y-4">
                  {[
                    'Contact us with your staffing requirements\u2014tell us the roles, industry, number of workers, and timeline.',
                    'We source, screen, and match qualified candidates from our worker database.',
                    'Approved workers arrive on-site ready to work. We handle payroll, WCB, and all administration.',
                    'Ongoing support\u2014we check in regularly and replace workers if needed at no extra cost.',
                  ].map((step, i) => (
                    <li key={i} className="flex items-start">
                      <span className="flex-shrink-0 w-8 h-8 bg-[#003366] text-white rounded-full flex items-center justify-center font-bold mr-4 mt-0.5">{i + 1}</span>
                      <p className="text-gray-700 leading-relaxed">{step}</p>
                    </li>
                  ))}
                </ol>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-[#003366] mb-6">For Job Seekers</h3>
                <ol className="space-y-4">
                  {[
                    'Register by filling out our contact form or emailing your resume to resumeworknation@gmail.com.',
                    'Our team reviews your qualifications, experience, and availability.',
                    'We match you with available positions that fit your skills, location, and schedule preferences.',
                    'Show up, work hard, get paid. Perform well and we\u2019ll keep the opportunities coming.',
                  ].map((step, i) => (
                    <li key={i} className="flex items-start">
                      <span className="flex-shrink-0 w-8 h-8 bg-[#003366] text-white rounded-full flex items-center justify-center font-bold mr-4 mt-0.5">{i + 1}</span>
                      <p className="text-gray-700 leading-relaxed">{step}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </section>

        <CTABanner />
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
